import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Trash2,
  Edit2,
  X,
  Image as ImageIcon,
  Calendar,
  User,
  Upload,
  MapPin,
  Video,
  Clock,
  Tag,
  Loader2,
  Search,
} from "lucide-react";
import marathiService from "../../services/marathiService";

// Custom Alert Component
const Alert = ({ children, variant, className = "" }) => {
  const baseStyles = "flex w-full rounded-lg border p-4";
  const variantStyles =
    variant === "destructive"
      ? "border-red-200 bg-red-50 text-red-600"
      : "border-gray-200 bg-gray-50 text-gray-600";

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = "" }) => (
  <div className={`text-sm ${className}`}>{children}</div>
);

const MarathiSpeeches = () => {
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSpeech, setSelectedSpeech] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(12);

  const [formData, setFormData] = useState({
    title: "",
    thumbnail: "",
    content: [""],
    date: new Date().toISOString().split("T")[0],
    speaker: "",
    venue: "",
    videoLink: "",
    duration: "",
    images: [],
    topics: [],
  });

  // Success message auto-dismiss timer
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchSpeeches = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await marathiService.speeches.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
      });

      console.log("Fetched speeches response:", response);

      // Handle different response structures
      if (Array.isArray(response)) {
        setSpeeches(response);
        setTotalPages(Math.ceil(response.length / itemsPerPage) || 1);
      } else if (response && response.data) {
        setSpeeches(response.data);
        setTotalPages(
          response.totalPages ||
            Math.ceil(response.data.length / itemsPerPage) ||
            1
        );
      } else {
        console.error("Unexpected response structure:", response);
        setError("Invalid data format from server");
        setSpeeches([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching speeches:", err);
      setError(err.message || "Failed to fetch speeches");
      setSpeeches([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchSpeeches();
  }, [fetchSpeeches]);

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === "content") {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData((prev) => ({ ...prev, content: newContent }));
    } else if (name === "topics") {
      const topicsArray = value
        .split(",")
        .map((topic) => topic.trim())
        .filter(Boolean);
      setFormData((prev) => ({ ...prev, topics: topicsArray }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddParagraph = () => {
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, ""],
    }));
  };

  const handleRemoveParagraph = (index) => {
    setFormData((prev) => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(
          `${
            type === "thumbnail" ? "Thumbnail" : "Image"
          } size should be less than 5MB`
        );
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "thumbnail") {
          setFormData((prev) => ({ ...prev, thumbnail: reader.result }));
        } else if (type === "image") {
          if (formData.images.length >= 5) {
            setError("Maximum 5 images are allowed");
            return;
          }
          
          // Compress the image before adding
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set max dimensions
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            
            let width = img.width;
            let height = img.height;
            
            if (width > height) {
                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }
            } else {
                if (height > MAX_HEIGHT) {
                    width *= MAX_HEIGHT / height;
                    height = MAX_HEIGHT;
                }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            ctx.drawImage(img, 0, 0, width, height);
            
            // Get compressed image
            const compressedImage = canvas.toDataURL('image/jpeg', 0.7); // 0.7 quality
            
            setFormData((prev) => ({
              ...prev,
              images: [...prev.images, compressedImage]
            }));
          };
          img.src = reader.result;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setError("");

    if (!formData.title.trim()) {
      setError("Title is required");
      return false;
    }

    if (!formData.thumbnail) {
      setError("Thumbnail is required");
      return false;
    }

    if (!formData.content[0]) {
      setError("Content is required");
      return false;
    }

    if (!formData.speaker.trim()) {
      setError("Speaker is required");
      return false;
    }

    if (!formData.videoLink.trim()) {
      setError("Video link is required");
      return false;
    }

    // Enhanced URL validation that handles all YouTube formats and other platforms
    const urlRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/|vimeo\.com\/|dailymotion\.com\/video\/|facebook\.com\/.+\/videos\/|instagram\.com\/p\/|streamable\.com\/)/i;

    if (!urlRegex.test(formData.videoLink)) {
      setError("Please enter a valid video URL from supported platforms");
      return false;
    }

    if (formData.images.length > 5) {
      setError("Maximum 5 images are allowed");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError("");

      const formDataToSend = {
        ...formData,
        content: formData.content.filter(
          (paragraph) => paragraph.trim() !== ""
        ),
      };

      console.log("Submitting speech data:", formDataToSend);

      if (selectedSpeech) {
        await marathiService.speeches.update(
          selectedSpeech._id,
          formDataToSend
        );
        setSuccess("Speech updated successfully!");
      } else {
        await marathiService.speeches.create(formDataToSend);
        setSuccess("Speech created successfully!");
      }

      await fetchSpeeches();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error saving speech:", error);
      setError(error.message || "Failed to save speech. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (speech) => {
    setSelectedSpeech(speech);
    setFormData({
      title: speech.title || "",
      thumbnail: speech.thumbnail || "",
      content: speech.content || [""],
      date: speech.date
        ? new Date(speech.date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      speaker: speech.speaker || "",
      venue: speech.venue || "",
      videoLink: speech.videoLink || "",
      duration: speech.duration || "",
      images: speech.images || [],
      topics: speech.topics || [],
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await marathiService.speeches.delete(selectedSpeech._id);
      setSuccess("Speech deleted successfully!");
      fetchSpeeches();
      setShowDeleteModal(false);
      setSelectedSpeech(null);
    } catch (error) {
      console.error("Error deleting speech:", error);
      setError(error.message || "Failed to delete speech");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      thumbnail: "",
      content: [""],
      date: new Date().toISOString().split("T")[0],
      speaker: "",
      venue: "",
      videoLink: "",
      duration: "",
      images: [],
      topics: [],
    });
    setSelectedSpeech(null);
  };

  const getImageUrl = (image) => {
    if (!image) return '/placeholder.jpg';
    
    // If it's a base64 data URL, return it as is
    if (typeof image === 'string' && image.startsWith('data:image/')) {
      return image;
    }
    
    // If it's a file object, create an object URL
    if (image instanceof File || image instanceof Blob) {
      return URL.createObjectURL(image);
    }
    
    // If it's a string but not a data URL (assuming it's a path)
    if (typeof image === 'string') {
      // If it's already a full URL, return it as is
      if (image.startsWith('http')) return image;
      
      // For file paths, generate the correct URL
      // Remove any "api/" prefix if present
      const path = image.replace(/^api\//, '');
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5500';
      return `${baseUrl}/${path}`;
    }
    
    // Fallback to placeholder
    return '/placeholder.jpg';
  };

  const formatVideoLink = (url) => {
    // Try to get video thumbnail or preview
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop() 
        : url.includes('v=') 
          ? url.split('v=')[1].split('&')[0]
          : null;
      
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      }
    }
    
    // Default thumbnail for other video platforms
    return '/video-thumbnail-placeholder.jpg';
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    fetchSpeeches();
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Marathi Speeches Management
        </h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          New Speech
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Search Bar */}
      <form onSubmit={handleSearchSubmit} className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search speeches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500"
          >
            <Search size={20} />
          </button>
        </div>
      </form>

      {/* Speeches List */}
      {loading && !showModal && !showDeleteModal ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      ) : speeches.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">
            {searchTerm 
              ? `No speeches found matching "${searchTerm}". Try a different search term.` 
              : "No speeches found. Create one to get started!"}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {speeches.map((speech) => (
              <div
                key={speech._id}
                className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow"
              >
                <div className="h-48 overflow-hidden relative group">
                  <img
                    src={speech.thumbnail ? getImageUrl(speech.thumbnail) : formatVideoLink(speech.videoLink)}
                    alt={speech.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder.jpg";
                    }}
                  />
                  {speech.videoLink && (
                    <a
                      href={speech.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Video className="text-white" size={48} />
                    </a>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {speech.title}
                  </h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <User size={16} className="mr-2" />
                      {speech.speaker}
                    </div>
                    {speech.venue && (
                      <div className="flex items-center text-gray-600">
                        <MapPin size={16} className="mr-2" />
                        {speech.venue}
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      {new Date(speech.date).toLocaleDateString()}
                    </div>
                    {speech.duration && (
                      <div className="flex items-center text-gray-600">
                        <Clock size={16} className="mr-2" />
                        {speech.duration}
                      </div>
                    )}
                  </div>
                  {speech.topics && speech.topics.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {speech.topics.map((topic, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs rounded-md"
                        >
                          <Tag size={12} className="mr-1" />
                          {topic}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <a
                      href={speech.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-red-500 hover:text-red-600"
                    >
                      <Video size={16} className="mr-1" />
                      Watch Video
                    </a>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(speech)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        aria-label="Edit speech"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedSpeech(speech);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        aria-label="Delete speech"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <nav className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Previous
                </button>
                
                <span className="px-3 py-1">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Last
                </button>
              </nav>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedSpeech ? "Edit Speech" : "Create New Speech"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Required Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={typeof formData.thumbnail === 'string' 
                          ? getImageUrl(formData.thumbnail) 
                          : URL.createObjectURL(formData.thumbnail)}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">
                          Upload thumbnail
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "thumbnail")}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Content Paragraphs */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">
                      Content *
                    </label>
                    <button
                      type="button"
                      onClick={handleAddParagraph}
                      className="text-red-500 hover:text-red-600"
                    >
                      + Add Paragraph
                    </button>
                  </div>
                  {formData.content.map((paragraph, index) => (
                    <div key={index} className="relative">
                      <textarea
                        name="content"
                        value={paragraph}
                        onChange={(e) => handleInputChange(e, index)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[100px]"
                        required
                      />
                      {formData.content.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveParagraph(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-600"
                          aria-label="Remove paragraph"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Speaker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Speaker *
                  </label>
                  <input
                    type="text"
                    name="speaker"
                    value={formData.speaker}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Venue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Venue
                  </label>
                  <input
                    type="text"
                    name="venue"
                    value={formData.venue}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                {/* Video Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Link *
                  </label>
                  <input
                    type="url"
                    name="videoLink"
                    value={formData.videoLink}
                    onChange={handleInputChange}
                    placeholder="Examples: https://youtu.be/abc123 or https://www.youtube.com/watch?v=abc123"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Supported formats: YouTube (youtube.com or youtu.be), Vimeo,
                    Dailymotion, Facebook, Instagram, Streamable
                  </p>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="e.g., 1h 30min"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                {/* Additional Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={typeof image === 'string' 
                            ? getImageUrl(image) 
                            : URL.createObjectURL(image)}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index),
                            }));
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          aria-label="Remove image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "image")}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Topics */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topics
                  </label>
                  <input
                    type="text"
                    name="topics"
                    value={formData.topics.join(", ")}
                    onChange={handleInputChange}
                    placeholder="Enter topics separated by commas"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <Loader2 className="animate-spin mr-2" size={20} />
                        Saving...
                      </span>
                    ) : (
                      selectedSpeech ? "Update Speech" : "Create Speech"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Delete Speech
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedSpeech?.title}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center">
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarathiSpeeches;